import React from 'react';
import { Mail, Send, Trash2 } from 'lucide-react';
import type { SupplierInvite, SupplierPortalRole, SupplierWorkFlag } from '../../../types';
import { supplierInviteStatusLabel } from '../../../hooks/useVendorOrgEditor';
import {
  parseSupplierPortalRole,
  parseSupplierWorkFlags,
  supplierPortalRoleLabel,
  supplierWorkFlagLabel,
} from '../../../utils/supplierWorkFlags';

export interface VendorInvitePanelProps {
  invites: SupplierInvite[];
  inviteName: string;
  inviteEmail: string;
  invitePortalRole: SupplierPortalRole;
  inviteWorkFlags: SupplierWorkFlag[];
  inviteSending: boolean;
  inviteMessage: string | null;
  onInviteNameChange: (v: string) => void;
  onInviteEmailChange: (v: string) => void;
  onInvitePortalRoleChange: (v: SupplierPortalRole) => void;
  onInviteWorkFlagsChange: (v: SupplierWorkFlag[]) => void;
  onSendInvite: () => void;
  onOpenUnifiedInvite?: () => void;
  onRevoke: (id: string) => void;
  onDelete: (id: string) => void;
}

function inviteWorkSummary(inv: SupplierInvite): string {
  const flags = parseSupplierWorkFlags(inv.supplierDesignations);
  const work = flags.length ? flags.map(supplierWorkFlagLabel).join(' · ') : 'Work unset';
  return `${supplierPortalRoleLabel(parseSupplierPortalRole(inv.supplierPortalRole))} · ${work}`;
}

const VendorInvitePanel: React.FC<VendorInvitePanelProps> = ({
  invites,
  inviteName,
  inviteEmail,
  invitePortalRole,
  inviteWorkFlags,
  inviteSending,
  inviteMessage,
  onInviteNameChange,
  onInviteEmailChange,
  onInvitePortalRoleChange,
  onInviteWorkFlagsChange,
  onSendInvite,
  onOpenUnifiedInvite,
  onRevoke,
  onDelete,
}) => (
  <section className="vm-ops-section">
    <h3 className="vm-ops-section-title">
      <Mail size={14} aria-hidden /> Portal users
    </h3>
    <p className="vm-ops-hint">
      Creates a vendor login and emails a temporary password when SMTP is configured.
      Choose Owner (quotes and totals) or Employee (files and production only), plus Timber / CNC work.
    </p>

    {onOpenUnifiedInvite && (
      <div className="vm-invite-form">
        <button
          type="button"
          className="ops-new-btn vm-invite-send"
          onClick={onOpenUnifiedInvite}
        >
          <Send size={13} aria-hidden />
          Invite portal user
        </button>
      </div>
    )}

    <div className="vm-invite-form vm-invite-form--full" hidden={!!onOpenUnifiedInvite}>
      <input
        type="text"
        className="ops-modal-input"
        placeholder="Full name"
        value={inviteName}
        onChange={(e) => onInviteNameChange(e.target.value)}
      />
      <input
        type="email"
        className="ops-modal-input"
        placeholder="Email"
        value={inviteEmail}
        onChange={(e) => onInviteEmailChange(e.target.value)}
      />
      <div className="vm-invite-subgroups">
        <div className="vm-invite-subgroup">
          <span className="vm-invite-subgroup-label">Access</span>
          <div className="vm-invite-toggles">
            {(['owner', 'employee'] as SupplierPortalRole[]).map((level) => (
              <button
                key={level}
                type="button"
                className={`vm-invite-toggle${invitePortalRole === level ? ' is-active' : ''}`}
                onClick={() => onInvitePortalRoleChange(level)}
              >
                {supplierPortalRoleLabel(level)}
              </button>
            ))}
          </div>
        </div>
        <div className="vm-invite-subgroup">
          <span className="vm-invite-subgroup-label">Work</span>
          <div className="vm-invite-toggles">
            {(['timber_supplier', 'cnc_supplier'] as SupplierWorkFlag[]).map((flag) => {
              const active = inviteWorkFlags.includes(flag);
              return (
                <button
                  key={flag}
                  type="button"
                  className={`vm-invite-toggle${active ? ' is-active' : ''}`}
                  onClick={() =>
                    onInviteWorkFlagsChange(
                      active ? inviteWorkFlags.filter((x) => x !== flag) : [...inviteWorkFlags, flag],
                    )
                  }
                >
                  {supplierWorkFlagLabel(flag)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="ops-new-btn vm-invite-send"
        disabled={inviteSending || !inviteName.trim() || !inviteEmail.trim() || inviteWorkFlags.length === 0}
        onClick={() => void onSendInvite()}
      >
        <Send size={13} aria-hidden />
        {inviteSending ? 'Sending…' : 'Send invite'}
      </button>
    </div>
    {inviteMessage && <p className="vm-banner">{inviteMessage}</p>}

    {invites.length === 0 ? (
      <p className="vm-empty-inline">No portal users yet.</p>
    ) : (
      <ul className="vm-invite-list">
        {invites.map((inv) => (
          <li key={inv.id} className="vm-invite-row">
            <div className="vm-invite-row-main">
              <span className="vm-invite-name">{inv.name}</span>
              <span className="vm-invite-email">{inv.email}</span>
              <span className="vm-invite-meta">{inviteWorkSummary(inv)}</span>
            </div>
            <span className={`vm-status-pill vm-status-pill--${inv.status}`}>
              {supplierInviteStatusLabel(inv.status)}
            </span>
            <div className="vm-invite-row-actions">
              {inv.status === 'pending' && inv.id && (
                <button type="button" className="ops-hide-completed-btn" onClick={() => onRevoke(inv.id!)}>
                  Revoke
                </button>
              )}
              {inv.id && (
                <button
                  type="button"
                  className="ops-hide-completed-btn vm-invite-delete"
                  title="Remove invite record"
                  onClick={() => {
                    if (
                      !window.confirm(
                        'Delete this invite from the list? If an account was already created, the user can still sign in.',
                      )
                    )
                      return;
                    onDelete(inv.id!);
                  }}
                >
                  <Trash2 size={12} aria-hidden /> Delete
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export default VendorInvitePanel;
